import { useCallback, RefObject } from 'react'
import type { MapRef } from 'react-map-gl'
import type MapboxDraw from '@mapbox/mapbox-gl-draw'
import mapboxgl from 'mapbox-gl'
import type { MapLayerMouseEvent } from 'mapbox-gl'
import { formatZoneTooltip } from '../../../utils/mapHelpers'
import type { Zone } from '../../../../zones/types'

interface UseMapClickHandlerParams {
  mapRef: RefObject<MapRef | null>
  drawRef: RefObject<MapboxDraw | null>
  popupRef: RefObject<mapboxgl.Popup | null>
  justFinishedDraggingRef: RefObject<boolean>
  pendingGridPlacementRef: RefObject<[number, number] | null>
  drawingMode: 'draw' | 'grid' | 'view'
  editingZoneId: string | null
  editMode: 'polygon' | 'grid' | null
  gridSize: number
  zonesLayerId: string
  gridCellLayerId: string
  getZoneById: (id: string) => Zone | undefined
  handleSetSelectedZone: (zone: Zone | null) => void
  readonly onGridCellPositionChange?: (
    position: [number, number] | null
  ) => void
  readonly onEditModeChange?: (mode: 'polygon' | 'grid' | null) => void
  readonly blockMapInteractionsRef?: React.MutableRefObject<{
    blocked: boolean
    x: number
    y: number
    time: number
  }>
}

export function useMapClickHandler({
  mapRef,
  drawRef,
  popupRef,
  justFinishedDraggingRef,
  pendingGridPlacementRef,
  drawingMode,
  editingZoneId,
  editMode,
  gridSize,
  zonesLayerId,
  gridCellLayerId,
  getZoneById,
  handleSetSelectedZone,
  onGridCellPositionChange,
  onEditModeChange,
  blockMapInteractionsRef,
}: UseMapClickHandlerParams) {
  const isGhostClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (!blockMapInteractionsRef?.current) return false
      const { x, y, time } = blockMapInteractionsRef.current
      const now = Date.now()
      const clickX = e.originalEvent?.clientX ?? 0
      const clickY = e.originalEvent?.clientY ?? 0

      if (now - time < 500) {
        const dist = Math.sqrt(
          Math.pow(clickX - x, 2) + Math.pow(clickY - y, 2)
        )
        return dist < 60
      }
      return false
    },
    [blockMapInteractionsRef]
  )

  const shouldIgnoreClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (!mapRef.current) return true
      if (justFinishedDraggingRef.current) return true

      const mapCanvas = mapRef.current.getMap().getCanvas()
      const clickTarget = e.originalEvent?.target as Node
      return (
        clickTarget &&
        clickTarget !== mapCanvas &&
        !mapCanvas.contains(clickTarget)
      )
    },
    [mapRef, justFinishedDraggingRef]
  )

  const handleEditModeClick = useCallback(
    (e: MapLayerMouseEvent, map: mapboxgl.Map) => {
      if (!editingZoneId) return false

      const zoneFeatures = map.queryRenderedFeatures(e.point, {
        layers: [zonesLayerId],
      })
      const clickedOnPolygon = zoneFeatures.some(
        f => f.properties?.['zoneId'] === editingZoneId
      )

      let clickedOnGridCell = false
      if (map.getLayer(gridCellLayerId)) {
        try {
          const gridFeatures = map.queryRenderedFeatures(e.point, {
            layers: [gridCellLayerId],
          })
          clickedOnGridCell = gridFeatures.length > 0
        } catch {
          /* Layer might not be ready */
        }
      }

      if (editMode === 'grid' && clickedOnPolygon) {
        onEditModeChange?.('polygon')
        return true
      }

      if (editMode === 'polygon') {
        if (clickedOnGridCell) {
          onEditModeChange?.('grid')
          return true
        }
        return !!drawRef.current
      }

      return false
    },
    [
      editingZoneId,
      editMode,
      zonesLayerId,
      gridCellLayerId,
      onEditModeChange,
      drawRef,
    ]
  )

  const handleGridPlacement = useCallback(
    (map: mapboxgl.Map, clickPosition: [number, number]) => {
      if (!onGridCellPositionChange) return false

      const currentZoomLevel = map.getZoom()
      const is100mGrid = gridSize === 0.1
      const minZoom = 14

      if (is100mGrid && currentZoomLevel < minZoom) {
        pendingGridPlacementRef.current = clickPosition
        map.once('moveend', () => {
          if (!mapRef.current) return
          const pendingPosition = pendingGridPlacementRef.current
          pendingGridPlacementRef.current = null
          if (pendingPosition) onGridCellPositionChange(pendingPosition)
        })
        map.flyTo({ center: clickPosition, zoom: minZoom, duration: 500 })
        return true
      }

      onGridCellPositionChange(clickPosition)
      return true
    },
    [gridSize, pendingGridPlacementRef, mapRef, onGridCellPositionChange]
  )

  const handleGridModeClick = useCallback(
    (e: MapLayerMouseEvent, map: mapboxgl.Map) => {
      if (
        drawingMode !== 'grid' ||
        editingZoneId ||
        justFinishedDraggingRef.current
      )
        return false

      const zoneFeatures = map.queryRenderedFeatures(e.point, {
        layers: [zonesLayerId],
      })
      let gridCellFeatures: GeoJSON.Feature[] = []

      if (map.getLayer(gridCellLayerId)) {
        try {
          gridCellFeatures = map.queryRenderedFeatures(e.point, {
            layers: [gridCellLayerId],
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.debug('Grid cell layer not ready:', error)
        }
      }

      if (zoneFeatures.length === 0 && gridCellFeatures.length === 0) {
        const clickPosition: [number, number] = [e.lngLat.lng, e.lngLat.lat]
        return handleGridPlacement(map, clickPosition)
      }

      return false
    },
    [
      drawingMode,
      editingZoneId,
      justFinishedDraggingRef,
      zonesLayerId,
      gridCellLayerId,
      handleGridPlacement,
    ]
  )

  const handleZoneClick = useCallback(
    (e: MapLayerMouseEvent, map: mapboxgl.Map) => {
      if (editingZoneId) return false

      const features = map.queryRenderedFeatures(e.point, {
        layers: [zonesLayerId],
      })
      if (features.length > 0) {
        const feature = features[0]
        if (feature) {
          const zoneId = feature.properties?.['zoneId'] as string
          const zone = getZoneById(zoneId)

          if (zone) {
            handleSetSelectedZone(zone)

            if (popupRef.current) popupRef.current.remove()
            const popup = new mapboxgl.Popup({ closeOnClick: false })
              .setLngLat(e.lngLat)
              .setHTML(formatZoneTooltip(zone))
              .addTo(map)
            popupRef.current = popup
            return true
          }
        }
      }

      return false
    },
    [editingZoneId, zonesLayerId, getZoneById, handleSetSelectedZone, popupRef]
  )

  return useCallback(
    async (e: MapLayerMouseEvent) => {
      if (isGhostClick(e)) return
      if (shouldIgnoreClick(e)) return

      const map = mapRef.current!.getMap()

      if (handleEditModeClick(e, map)) return
      if (handleGridModeClick(e, map)) return
      if (handleZoneClick(e, map)) return

      if (popupRef.current) {
        popupRef.current.remove()
        popupRef.current = null
      }
    },
    [
      isGhostClick,
      shouldIgnoreClick,
      handleEditModeClick,
      handleGridModeClick,
      handleZoneClick,
      mapRef,
      popupRef,
    ]
  )
}
